import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  convertDateToTimeZone,
  DateFormatter,
  getObjValueByStringKey,
} from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import BaseGrid from 'components/atoms/base-grid';
import BaseLoader from 'components/atoms/base-loader';
import classNames from 'classnames';
import FadeAnimation from 'components/atoms/base-animations/fade';
import TemperatureIcon from 'components/atoms/icons/temperature-icon';
import { useWeather } from 'infrastructure/hooks/use-weather';
import { createPortal } from 'react-dom';
import { ASSIGN_TOOL_ID } from 'infrastructure/consts';

import s from './styles.module.scss';
import WeatherTime from './time';
import WeatherIcon from './icon';

const roundNumber = (num: number): number =>
  typeof num === 'number' ? Math.round(num) : num;

interface IProps {
  city?: string | null;
  state?: string | null;
  timezone?: string | null;
}

const WeatherWidget: React.FC<IProps> = (props) => {
  const { t } = useTranslation();
  const { city, state, timezone } = props;

  const [opened, setOpened] = useState(false);
  const [hasAssignToolNearby, setHasAssignToolNearby] = useState(false);

  const { data: weather, isLoading: loading } = useWeather({
    city,
    state,
    canLoad: opened,
  });

  const ROOT_ELEMENT = document.getElementById('root');
  useEffect(() => {
    const hasAssignTool = !!ROOT_ELEMENT?.querySelector(`#${ASSIGN_TOOL_ID}`);
    setHasAssignToolNearby(hasAssignTool);
  }, []);
  const hasWeather = !!weather;

  const toggleOpen = () => {
    setOpened(!opened);
  };

  const date = useMemo(() => {
    let d = new Date();
    if (timezone) {
      d = convertDateToTimeZone(d, timezone);
    }
    return d;
  }, [timezone]);

  const locationName = useMemo(() => {
    let name = ``;
    if (city) {
      name += city;
    }
    if (state) {
      name += `, ${state}`;
    }
    return name;
  }, [city, state]);

  const safeValue = useCallback(
    (path: string): string | number => {
      const value = getObjValueByStringKey(weather, path);
      return roundNumber(value) ?? '-';
    },
    [weather],
  );

  return createPortal(
    <div
      id="weather-widget"
      data-move-down={hasAssignToolNearby}
      className={classNames(s.wrapper, {
        [s.opened]: opened,
      })}
    >
      <FadeAnimation uniqueKey={hasWeather.toString()}>
        <div className={s.btn} onClick={toggleOpen}>
          {weather ? (
            <WeatherIcon type={weather?.weatherText} />
          ) : (
            <TemperatureIcon />
          )}
        </div>
      </FadeAnimation>

      <div onClick={toggleOpen}>
        <FadeAnimation uniqueKey={hasWeather.toString()}>
          <BaseGrid className={s.body} rowGap={8}>
            <BaseLoader loading={loading} dataCy="weather-loader">
              <div className={s.card}>
                <div>
                  <p className={s.location}>{locationName}</p>
                  <div className={s.flex}>
                    <p className={s.large}>{`${safeValue('temp')}°F`}</p>
                    <p className={s.weather}>
                      {weather && (
                        <WeatherIcon
                          type={weather?.weatherText}
                          className={s.absolute}
                        />
                      )}
                      <span>{weather && weather?.weatherText} </span>
                      <span>
                        {`${t('labels.hight')}: ${safeValue('temp_max')}° 
                      ${t('labels.low')}: ${safeValue('temp_min')}°`}
                      </span>
                      <span>
                        {`${t('labels.humidity')}: ${safeValue('humidity')}%`}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              <BaseGrid className={s.card} columns={1} rowGap={16}>
                <p className={s.time}>
                  {t('labels.patientTime')}
                  <span>
                    {DateFormatter({
                      format: DateFormats['EEE, MMM d'],
                      date,
                    })}
                  </span>
                </p>
                <p className={s.large}>
                  {timezone && opened && <WeatherTime timezone={timezone} />}
                </p>
              </BaseGrid>
            </BaseLoader>
          </BaseGrid>
        </FadeAnimation>
      </div>
    </div>,
    ROOT_ELEMENT!,
  );
};

export default WeatherWidget;
